/**
 * HopeContextService - Provides context-aware AI responses
 *
 * Tracks which page/feature the patient is using and provides
 * context to Hope for more relevant and helpful responses.
 */

export interface PatientContext {
  currentPage: string;
  pageName: string;
  timeOnPage: number; // seconds
  lastInteraction: Date;
  isStuck?: boolean; // User hasn't interacted in a while
  contextualGreeting?: string;
  suggestedTopics?: string[];
}

/**
 * Get context-aware information based on current page
 */
export const getPageContext = (pathname: string): PatientContext => {
  const now = new Date();

  // Parse pathname to determine context
  if (pathname.includes('dashboard')) {
    return {
      currentPage: 'dashboard',
      pageName: 'Dashboard',
      timeOnPage: 0,
      lastInteraction: now,
      contextualGreeting: "I see you're on your dashboard. How can I help you navigate MyClinicCore today?",
      suggestedTopics: [
        'Upcoming appointments',
        'Messages from your provider',
        'Recent resources',
      ],
    };
  }

  if (pathname.includes('appointment') || pathname.includes('book')) {
    return {
      currentPage: 'appointments',
      pageName: 'Appointments',
      timeOnPage: 0,
      lastInteraction: now,
      contextualGreeting: "I see you're working with appointments. Need help scheduling or preparing for your next session?",
      suggestedTopics: [
        'How to prepare for a therapy session',
        'What to expect in your first appointment',
        'Managing appointment anxiety',
      ],
    };
  }

  if (pathname.includes('forms') || pathname.includes('documents')) {
    return {
      currentPage: 'forms',
      pageName: 'Forms & Documents',
      timeOnPage: 0,
      lastInteraction: now,
      contextualGreeting: "Working on forms? I can help clarify any questions or explain what information is needed.",
      suggestedTopics: [
        'Why certain information is needed',
        'Understanding intake forms',
        'Privacy and confidentiality',
      ],
    };
  }

  if (pathname.includes('resources')) {
    return {
      currentPage: 'resources',
      pageName: 'Resources',
      timeOnPage: 0,
      lastInteraction: now,
      contextualGreeting: "Exploring coping strategies and resources? I can help explain any techniques or answer questions.",
      suggestedTopics: [
        'Breathing exercises for anxiety',
        'Grounding techniques',
        'Sleep hygiene tips',
        'Mindfulness practices',
      ],
    };
  }

  if (pathname.includes('messages') || pathname.includes('secure-messaging')) {
    return {
      currentPage: 'messages',
      pageName: 'Secure Messages',
      timeOnPage: 0,
      lastInteraction: now,
      contextualGreeting: "Looking at your messages? I'm here if you need support while waiting for your provider's response.",
      suggestedTopics: [
        'How to communicate effectively with your provider',
        'When to send a message vs. call',
        'Managing expectations for response times',
      ],
    };
  }

  if (pathname.includes('profile') || pathname.includes('settings')) {
    return {
      currentPage: 'profile',
      pageName: 'Profile & Settings',
      timeOnPage: 0,
      lastInteraction: now,
      contextualGreeting: "Updating your profile? Let me know if you have questions about any of the settings.",
      suggestedTopics: [
        'Privacy settings',
        'Notification preferences',
        'Insurance information',
      ],
    };
  }

  if (pathname.includes('homework') || pathname.includes('assignments')) {
    return {
      currentPage: 'homework',
      pageName: 'Homework & Assignments',
      timeOnPage: 0,
      lastInteraction: now,
      contextualGreeting: "Working on your therapy homework? I can help you understand the exercises or talk through any challenges.",
      suggestedTopics: [
        'Understanding my homework assignment',
        'Tips for completing exercises',
        'What to do if homework feels overwhelming',
      ],
    };
  }

  if (pathname.includes('measures') || pathname.includes('assessments')) {
    return {
      currentPage: 'measures',
      pageName: 'Measures & Assessments',
      timeOnPage: 0,
      lastInteraction: now,
      contextualGreeting: "I see you're looking at your measures. These assessments help track your progress over time. Want me to explain what they mean?",
      suggestedTopics: [
        'What do my assessment scores mean?',
        'How is my progress tracked?',
        'Why do I take these assessments?',
      ],
    };
  }

  if (pathname.includes('treatment-plan') || pathname.includes('treatmentplan')) {
    return {
      currentPage: 'treatment-plan',
      pageName: 'Treatment Plan',
      timeOnPage: 0,
      lastInteraction: now,
      contextualGreeting: "Reviewing your treatment plan? I can help explain your goals and the steps your provider has outlined for your care.",
      suggestedTopics: [
        'Understanding my treatment goals',
        'What are the steps in my plan?',
        'How long will treatment take?',
      ],
    };
  }

  if (pathname.includes('billing') || pathname.includes('payment') || pathname.includes('invoice')) {
    return {
      currentPage: 'billing',
      pageName: 'Billing & Payments',
      timeOnPage: 0,
      lastInteraction: now,
      contextualGreeting: "Looking at billing? I can help answer general questions about payments and insurance.",
      suggestedTopics: [
        'Understanding my bill',
        'Insurance coverage questions',
        'Payment options',
      ],
    };
  }

  if (pathname.includes('video') || pathname.includes('telehealth') || pathname.includes('session')) {
    return {
      currentPage: 'video-session',
      pageName: 'Video Session',
      timeOnPage: 0,
      lastInteraction: now,
      contextualGreeting: "Getting ready for a video session? I can help you prepare or troubleshoot any technical issues.",
      suggestedTopics: [
        'How to prepare for a video session',
        'Troubleshooting audio/video',
        'What to expect in a telehealth session',
      ],
    };
  }

  if (pathname.includes('couples')) {
    return {
      currentPage: 'couples',
      pageName: 'Couples Therapy',
      timeOnPage: 0,
      lastInteraction: now,
      contextualGreeting: "I see you're in the couples therapy area. I can help with communication tips or session preparation.",
      suggestedTopics: [
        'Communication tips for couples',
        'Preparing for a couples session',
        'Understanding couples therapy goals',
      ],
    };
  }

  // Default context for unknown pages
  return {
    currentPage: 'unknown',
    pageName: 'MyClinicCore',
    timeOnPage: 0,
    lastInteraction: now,
    contextualGreeting: "Hi! Welcome to MyClinicCore app. How can I help you today?",
    suggestedTopics: [
      'Understanding your diagnosis',
      'Coping strategies',
      'Navigating the app',
    ],
  };
};

/**
 * Enhance user message with context for Hope
 * This adds invisible context to the message sent to Convai
 */
export const addContextToMessage = (
  userMessage: string,
  context: PatientContext
): string => {
  // For now, just return the user message as-is
  // In future, we could prepend context like:
  // "[Patient is on Appointments page] " + userMessage

  // But for simplicity and to avoid confusing the AI, we keep it clean
  return userMessage;
};

/**
 * Get proactive suggestions based on context
 * Hope can offer help if user seems stuck on a page
 */
export const getProactiveSuggestions = (context: PatientContext): string[] => {
  if (context.timeOnPage > 120 && !context.isStuck) {
    // User has been on page for 2+ minutes
    return [
      "I notice you've been here for a bit. Need any help?",
      "Can I clarify anything about this page?",
      "Feel free to ask if you have questions!",
    ];
  }

  return [];
};

/**
 * Detect if patient might be in distress based on behavior
 * (Rapid page switching, long time on crisis resources, etc.)
 */
export const detectDistressBehavior = (
  pageHistory: string[],
  timeSpent: number[]
): boolean => {
  // Simple heuristic: if user views crisis resources page for >60 seconds
  const lastPage = pageHistory[pageHistory.length - 1];
  const lastTime = timeSpent[timeSpent.length - 1];

  if (lastPage.includes('crisis') || lastPage.includes('emergency')) {
    if (lastTime > 60) {
      return true;
    }
  }

  // Rapid switching (more than 5 page changes in 1 minute)
  if (pageHistory.length > 5 && timeSpent.reduce((a, b) => a + b, 0) < 60) {
    return true;
  }

  return false;
};
